import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyModulesState } from '@features/Dashboard';
import { fetchTrainingOverview, type TrainingModulePlan } from '@features/Session';
import { trackEvent } from '@shared/analytics';
import { buildCramPath, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, CheckIcon, ChevronRightIcon, PlainField } from '@shared/ui';

/** Сколько слов берёт «На сегодня» по умолчанию — как порция из письма «Повторить 10 слов». */
const TODAY_COUNT = 10;
/** С какого числа модулей нужен поиск: меньше — список виден целиком. */
const SEARCH_FROM = 7;

type Tone = 'repeat' | 'done' | 'quiet';

/** Одна строка о модуле — сжатая сводка «пройдено сегодня · ещё · завтра» (решение владельца 2026-10-21). */
function rowStatus(module: TrainingModulePlan, t: (key: string, options?: Record<string, unknown>) => string): {
  tone: Tone;
  text: ReactNode;
} {
  const tomorrow = module.tomorrow > 0 ? ` · ${t('training.rowTomorrow', { count: module.tomorrow })}` : '';
  const fresh = module.new > 0 ? ` · ${t('training.rowNew', { count: module.new })}` : '';
  if (module.ready === 0) return { tone: 'quiet', text: t('training.rowEmpty') };
  if (!module.trained) return { tone: 'quiet', text: t('training.rowUntrained', { count: module.new }) };
  if (module.doneToday > 0 && module.repeat > 0) {
    return {
      tone: 'repeat',
      text: (
        <>
          {t('training.rowToday', { count: module.doneToday })} ·{' '}
          <b className="font-semibold text-fg">{t('training.rowMore', { count: module.repeat })}</b>
          {tomorrow}
        </>
      ),
    };
  }
  // Сколько ещё не учили — и после сегодняшней тренировки («тут можно написать, что 31 слово не выученное» —
  // владелец, 2026-10-21).
  if (module.doneToday > 0) {
    return { tone: 'done', text: t('training.rowDone', { count: module.doneToday }) + fresh + tomorrow };
  }
  if (module.repeat > 0) {
    return {
      tone: 'repeat',
      text: (
        <>
          <b className="font-semibold text-fg">{t('training.rowRepeat', { count: module.repeat })}</b>
          {fresh}
        </>
      ),
    };
  }
  return { tone: 'quiet', text: t('training.rowNothing') + (fresh || tomorrow) };
}

function TrainingRow({ module, onOpen }: { module: TrainingModulePlan; onOpen: () => void }) {
  const { t } = useTranslation();
  const { tone, text } = rowStatus(module, t);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 rounded-[16px] border border-solid border-line bg-card px-4 py-3 text-left transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-5 text-fg">{module.title}</span>
          <span className="flex-none text-[12px] leading-4 text-muted">{t('training.words', { count: module.ready })}</span>
        </span>
        <span className="mt-1 flex items-center gap-2 text-[13px] leading-[18px] text-muted">
          {tone === 'done' ? (
            <CheckIcon className="h-3 w-3 flex-none text-success" aria-hidden />
          ) : (
            <span
              aria-hidden
              className={cn('h-2 w-2 flex-none rounded-full', tone === 'repeat' ? 'bg-accent' : 'bg-white/[0.16]')}
            />
          )}
          <span className="min-w-0 truncate">{text}</span>
        </span>
      </span>
      <ChevronRightIcon className="h-4 w-4 flex-none text-muted transition-colors group-hover:text-fg" aria-hidden />
    </button>
  );
}

/**
 * «На чём потренируемся?» — сюда ведёт кнопка «Тренироваться» с главной. У каждого модуля одна строка: сколько
 * повторено сегодня, что осталось, что завтра (по истории тренировок, не по FSRS). Сверху — где есть что
 * повторить. Выбор модуля открывает конструктор с отмеченным «На сегодня»: сначала повторить, потом новые.
 */
export default function TrainingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const query = useQuery({
    queryKey: ['sessions', 'overview'],
    queryFn: fetchTrainingOverview,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const all = query.data ?? [];
  const needle = search.trim().toLowerCase();
  const modules = needle ? all.filter((module) => module.title.toLowerCase().includes(needle)) : all;

  const open = (module: TrainingModulePlan) => {
    const forToday = module.repeat + module.new > 0;
    trackEvent('training_module_chosen', {
      repeat: module.repeat,
      new: module.new,
      done_today: module.doneToday,
    });
    navigate(buildCramPath(module.moduleId) + (forToday ? `?cards=today&n=${TODAY_COUNT}` : ''), {
      state: { fromDashboard: true },
    });
  };

  return (
    <div className="mx-auto max-w-[800px] animate-fadeIn px-4 pb-24 pt-16 sm:px-6 nav:pt-10">
      <button onClick={() => navigate(paths.dashboard)} className="mb-2 min-h-11 text-sm text-muted hover:text-fg">
        ← {t('training.home')}
      </button>
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent">{t('training.stepModule')}</p>
      <h1 className="font-serif text-[28px] font-medium leading-tight tracking-[-0.02em] sm:text-[34px] nav:text-[40px]">
        {t('training.chooseModule')}
      </h1>
      <p className="mb-5 mt-2 text-sm leading-relaxed text-muted">{t('training.chooseSub')}</p>
      {all.length >= SEARCH_FROM && (
        <PlainField
          value={search}
          onChange={setSearch}
          placeholder={t('units.searchPlaceholder')}
          ariaLabel={t('units.searchPlaceholder')}
          className="mb-3 w-full rounded-pill border border-line bg-card px-5 py-3 text-sm text-fg outline-none focus:border-accent"
        />
      )}
      {query.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[68px] animate-skeleton rounded-[16px] bg-card" />
          ))}
        </div>
      ) : query.isError ? (
        <div role="alert" className="rounded-card border border-line bg-card p-6">
          <p className="mb-4 text-muted">{t('training.loadError')}</p>
          <Button onClick={() => void query.refetch()}>{t('training.retry')}</Button>
        </div>
      ) : all.length === 0 ? (
        <EmptyModulesState />
      ) : modules.length === 0 ? (
        <p role="status" className="py-10 text-center text-muted">
          {t('units.empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {modules.map((module) => (
            <TrainingRow key={module.moduleId} module={module} onOpen={() => open(module)} />
          ))}
        </div>
      )}
    </div>
  );
}
